import { useEffect, useRef, useState } from 'react';
import { Map as MapLibreMap, type LayerSpecification } from 'maplibre-gl';
import { setWorkerUrl } from 'maplibre-gl';
// Vite does not emit MapLibre's worker module (or the shared module it imports) in production builds, so
// the two files are copied from node_modules/maplibre-gl/dist into public/maplibre and served statically.
// Keep them in sync when upgrading maplibre-gl.
setWorkerUrl('/maplibre/maplibre-gl-worker.mjs');
import 'maplibre-gl/dist/maplibre-gl.css';
import type { AoMeta } from '../sim/types';
import { Projection } from './projection';

/** OpenFreeMap's hosted dark style: no key, no rate limit. */
export const DARK_STYLE = 'https://tiles.openfreemap.org/styles/dark';
/** Fallback if the dark style ever disappears; pair it with `filterCss` below. */
export const POSITRON_STYLE = 'https://tiles.openfreemap.org/styles/positron';
/** CSS filter that turns the light positron style into an ops-center dark basemap. */
export const POSITRON_DARK_FILTER = 'invert(1) hue-rotate(180deg) brightness(0.85) saturate(0.35)';

/** Free global digital elevation model (DEM) tiles, Mapzen terrarium encoding, no auth. */
const TERRARIUM_TILES = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

export interface MapReady {
  map: MapLibreMap;
  /**
   * The positioned element the Pixi overlay canvas is appended to, and the element overlay click
   * handling binds to. It is the map container's parent and shares its box exactly, so
   * `map.project` output is already in this element's coordinate space. The overlay lives here
   * rather than inside the map container so a CSS `filter` used to darken a light basemap cannot
   * recolour the symbols.
   */
  container: HTMLDivElement;
  projection: Projection;
}

export interface MapViewProps {
  meta: AoMeta;
  /**
   * Padding, in screen pixels, kept clear around the AO when the camera is fitted. A number pads every
   * side; the object form lets a stage reserve room for rails that float over the map.
   */
  fitPadding?: number | { top: number; bottom: number; left: number; right: number };
  /**
   * Called once the style has loaded and the camera is fitted to the AO. Return a cleanup
   * function (e.g. `overlay.destroy`) and it runs when the map is torn down.
   */
  onReady?: (ready: MapReady) => void | (() => void);
  styleUrl?: string;
  /** CSS filter applied to the map container (used only with the light positron style). */
  filterCss?: string;
  className?: string;
}

/**
 * Owns one MapLibre map fitted to an AO (area of operations), with a hillshade layer from free
 * terrarium DEM tiles. Children are rendered above the map inside the same positioned container,
 * and `onReady` hands the map, its container, and the AO projection to whatever draws on top.
 */
export function MapView({ meta, onReady, fitPadding = 24, styleUrl = DARK_STYLE, filterCss, className }: MapViewProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const readyRef = useRef(onReady);
  readyRef.current = onReady;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    const container = wrapRef.current;
    if (!host || !container) return;

    const projection = new Projection(meta);
    const aoBounds = projection.bounds(0);
    // The pan limit has to be loose enough that the whole AO still fits inside the viewport at the
    // camera's most zoomed-out position: MapLibre zooms *in* to honour maxBounds, so a tight limit on a
    // wide window silently crops the AO (and any rail padding asked for below).
    const padded = projection.bounds(0.6);

    const map = new MapLibreMap({
      container: host,
      style: styleUrl,
      bounds: aoBounds,
      fitBoundsOptions: { padding: fitPadding },
      maxBounds: padded,
      maxZoom: 16,
      minZoom: 8,
      attributionControl: { compact: true },
      // The overlay does its own hit-testing on the container, and a rotated map would make
      // north-up military graphics read wrong; keep the camera pan/zoom only.
      dragRotate: false,
      pitchWithRotate: false,
      touchZoomRotate: true,
    });
    map.touchZoomRotate.disableRotation();

    let cleanup: (() => void) | undefined;
    let disposed = false;

    const onStyleLoad = () => {
      if (disposed) return;
      try {
        if (!map.getSource('terrain-dem')) {
          map.addSource('terrain-dem', {
            type: 'raster-dem',
            tiles: [TERRARIUM_TILES],
            encoding: 'terrarium',
            tileSize: 256,
            maxzoom: 14,
            attribution: 'Elevation: Mapzen / AWS Terrain Tiles',
          });
        }
        if (!map.getLayer('ao-hillshade')) {
          // Put the hillshade under the first symbol (label) layer so place names stay legible.
          const firstSymbol = map.getStyle().layers?.find((l: LayerSpecification) => l.type === 'symbol')?.id;
          map.addLayer(
            {
              id: 'ao-hillshade',
              type: 'hillshade',
              source: 'terrain-dem',
              paint: {
                'hillshade-exaggeration': 0.5,
                'hillshade-shadow-color': '#000000',
                'hillshade-highlight-color': '#9fb4c7',
                'hillshade-accent-color': '#0b0f14',
              },
            },
            firstSymbol,
          );
        }
      } catch (e) {
        setError(String(e));
      }

      // OpenFreeMap's dark style paints runways and taxiways pure black, which reads as a drawn
      // graphic rather than terrain over desaturated hillshade (it is Bicycle Lake Army Airfield on the
      // NTC AO). Keep the airfield - a planner wants to see it - but in the basemap's own register.
      try {
        for (const layer of map.getStyle().layers ?? []) {
          if (!layer.id.startsWith('aeroway')) continue;
          if (layer.type === 'line') map.setPaintProperty(layer.id, 'line-color', '#1f2831');
          else if (layer.type === 'fill') map.setPaintProperty(layer.id, 'fill-color', '#161d25');
        }
      } catch {
        // A style without aeroway layers is fine; nothing to soften.
      }

      // MapLibre's `compact: true` attribution control adds `maplibregl-compact-show` to itself on
      // first paint, so the credit line starts open and lies straight across the assembly area labels
      // at the bottom of every area of operations. Collapsing it here leaves the round ⓘ, which still
      // opens on a click; `_updateCompact` only ever adds the classes to a control that does not
      // already carry `maplibregl-compact`, so it stays collapsed for the life of the map.
      try {
        container.querySelector('.maplibregl-ctrl-attrib')?.classList.remove('maplibregl-compact-show');
      } catch {
        // No attribution control (a style with nothing to credit): nothing to collapse.
      }

      map.fitBounds(aoBounds, { padding: fitPadding, duration: 0 });
      const ready = readyRef.current?.({ map, container, projection });
      if (typeof ready === 'function') cleanup = ready;
    };

    const onMapError = (e: unknown) => {
      // Tile 404s are noisy but harmless; only surface style/source failures.
      const msg = (e as { error?: { message?: string } })?.error?.message ?? '';
      if (msg && !/Failed to fetch|404/i.test(msg)) setError(msg);
    };

    map.on('error', onMapError);
    if (map.isStyleLoaded()) onStyleLoad();
    else map.once('load', onStyleLoad);

    return () => {
      disposed = true;
      cleanup?.();
      map.off('error', onMapError);
      map.remove();
    };
    // The map is rebuilt only when the AO or style changes; fitPadding is read at build time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, styleUrl]);

  return (
    <div ref={wrapRef} className={`relative h-full w-full ${className ?? ''}`}>
      {/* Inline positioning: maplibre-gl.css sets `.maplibregl-map { position: relative }`,
          which is loaded after Tailwind and would otherwise collapse a class-positioned container. */}
      <div
        ref={hostRef}
        style={{ position: 'absolute', inset: 0, ...(filterCss ? { filter: filterCss } : null) }}
      />
      {error && (
        <div className="mono absolute left-3 top-3 z-20 rounded bg-slate-900/90 px-2 py-1 text-xs text-warn">
          map: {error}
        </div>
      )}
    </div>
  );
}
